/**
 * Keeps a live subscription (e.g. a Firestore listener) alive across transient refusals.
 *
 * Right after the claims changed (family created, invite accepted) a listen can start a beat
 * before the backend sees the new token. It is refused once and would stay dead – the app then
 * hung on "Lädt …". `subscribe` gets an error callback; errors that `isTransient` accepts restart
 * the subscription with growing pauses (after `beforeRetry`, e.g. a token refresh), anything else
 * or too many tries ends it via `onGiveUp`. The returned function stops everything.
 */
export interface ResubscribeOptions<E> {
  isTransient: (error: E) => boolean;
  /** Runs before each retry; `attempt` starts at 1. */
  beforeRetry?: (attempt: number) => Promise<unknown>;
  onGiveUp?: (error: E) => void;
  maxRetries?: number;
  /** First pause in ms, doubled per attempt. */
  baseDelay?: number;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
}

export function resubscribing<E>(
  subscribe: (onError: (error: E) => void) => () => void,
  {
    isTransient,
    beforeRetry,
    onGiveUp,
    maxRetries = 5,
    baseDelay = 250,
    setTimer = (fn, ms) => setTimeout(fn, ms),
    clearTimer = (h) => clearTimeout(h as ReturnType<typeof setTimeout>),
  }: ResubscribeOptions<E>,
): () => void {
  let unsub: () => void = () => {};
  let timer: unknown;
  let tries = 0;
  let stopped = false;
  const start = () => {
    unsub = subscribe((error) => {
      if (stopped) return;
      if (!isTransient(error) || tries >= maxRetries) {
        onGiveUp?.(error);
        return;
      }
      tries++;
      const attempt = tries;
      timer = setTimer(
        () => {
          void (beforeRetry?.(attempt) ?? Promise.resolve())
            .catch(() => undefined)
            .finally(() => {
              if (!stopped) start();
            });
        },
        baseDelay * 2 ** (attempt - 1),
      );
    });
  };
  start();
  return () => {
    stopped = true;
    if (timer !== undefined) clearTimer(timer);
    unsub();
  };
}
